import { BlurEvent, Button, ClickEvent, Layout, LookupModelSelectionEvent, PrintableEvent, SlideoutDecorator, Snackbar, Table, TableRowDisplayEvent, Textbox, Toast } from "@mcleod/components";
import { Api, Currency, StringUtil } from "@mcleod/core";
import { AutogenLayoutElectronicAdvances } from "./autogen/AutogenLayoutElectronicAdvances";
import { WireUtil } from "./WireUtil";
import { LMEGeneralSettings } from "@mcleod/general";

interface ElectronicAdvancesOptions {
    movementId: string;
    movementBrokerage: boolean;
    orderId: string;
    driverId: string;
    driverName: string;
    validatedFrom: string;
    moveType: string;
    issueAdvanceCallback?: Function;
    manifestId?: string;
}

export class ElectronicAdvances extends AutogenLayoutElectronicAdvances {

    private _movementId: string;
    private _movementBrokerage: boolean;
    private _orderId: string;
    private _driverId: string;
    private _driverName: string;
    private _validatedFrom: string;
    private _moveType: string;
    private _issueAdvanceCallback: Function;
    private _manifestId: string;

    private buttonSave = this.createSaveButton();

    constructor(props?) {
        super(props);
        this._movementId = props?.movementId;
        this._issueAdvanceCallback = props?.issueAdvanceCallback;
        this._movementBrokerage = props?.movementBrokerage;
        this._orderId = props?.orderId;
        this._driverId = props?.driverId;
        this._driverName = props?.driverName;
        this._validatedFrom = props?.validatedFrom;
        this._moveType = props?.moveType;
        this._manifestId = props?.manifestId;
    }

    override async onLoad(): Promise<void> {
        const isBrokerageMovement: boolean = this._movementBrokerage;
        const validatedFrom = this._validatedFrom;
        this.showComponents(isBrokerageMovement);
        await this.searchData(isBrokerageMovement);
        await this.showAdvanceHistory(isBrokerageMovement);
        this.setUpBeforeLookupSearchPayAdvance(isBrokerageMovement);
        this.enhanceOrderIdBeforeLookUpModel();
        this.enhanceMovementIdBeforeLookUpModel();
        this.enhanceLookupModelForBrokerageMovementPayee(isBrokerageMovement);
        this.enhanceWireCodeBeforeLookupModel(validatedFrom, isBrokerageMovement);
        this.textboxWireCodeSelectionListener();
        this.textboxOrderIdSelectionListener();
        this.textboxMovementIdSelectionListener();
        this.addBlurListenerToTextboxWireCode();
        this.addBlurListenerToTextboxAmount();
        this.addblurListenerToPayeeId();
        this.addBlurListenerToTextboxId();
        if (this.textboxManifestId.visible) {
            this.textboxManifestId.visible = ("Y" == LMEGeneralSettings.getSingleton().getDispatchControl()["enable_manifesting"]);
        }
    }

    private showComponents(movementBrokerage: boolean) {
        this.textboxDriverName.visible = !movementBrokerage;
        this.textboxMovementId.visible = !movementBrokerage;
        this.textboxManifestId.visible = !movementBrokerage;
        this.textboxOrderId.visible = !movementBrokerage;
        this.textboxPayeeId.visible = movementBrokerage;
        this.textboxPayeeName.visible = !movementBrokerage;
        this.switchZmitWire.visible = !movementBrokerage;
        this.switchZmitWire.checked = !movementBrokerage ? this.switchZmitWire.checked : false;
        this.panelTotalDeduction.borderLeftWidth = movementBrokerage ? 0 : 1;
        this.panelOtherCharges.borderLeftWidth = movementBrokerage ? 0 : 1;
    }

    private async searchData(isBrokerageMovement: boolean) {
        const response = await this.mainDataSource.search({
            "movement_id": this._movementId,
            "movement_brokerage": isBrokerageMovement,
            "order_id": this._orderId,
            "driver_id": this._driverId,
            "driver_name": this._driverName,
            "validated_from": this._validatedFrom,
            "move_type": this._moveType,
            "manifest_id": this._manifestId
        });

        const data = response.modelRows[0].data;
        const isOrderIdRequiredInUserWirecode: boolean = data.wire_code_require_order;
        if (isOrderIdRequiredInUserWirecode) {
            this.textboxOrderId.allowDropdownBlank = false;
            this.textboxMovementId.allowDropdownBlank = false;
        }

        const whenPayeeIdIsNotEmpty = !StringUtil.isEmptyString(data.payee_id);
        const whenWireCodeIsNotEmpty = !StringUtil.isEmptyString(data.wirecode);
        const whenPayeeNameIsNotEmpty = !StringUtil.isEmptyString(data.payee_name);

        const isEnabled = isBrokerageMovement ? whenPayeeIdIsNotEmpty && whenWireCodeIsNotEmpty : whenPayeeNameIsNotEmpty && whenWireCodeIsNotEmpty;
        this.textboxAmount.enabled = isEnabled;
    }

    public static async showSlider(options: ElectronicAdvancesOptions) {
        const { movementId, movementBrokerage, orderId, driverId, driverName, validatedFrom, issueAdvanceCallback, moveType, manifestId } = options;
        const responsePermission = await WireUtil.userPermissionForWireCode();
        if (!responsePermission?.enabled_driver_advances || !responsePermission?.visible_driver_advances) {
            Snackbar.showWarningSnackbar(responsePermission.tooltip_message, {id: "wirecode-advance-validation"});
            return;
        }
        const slideOutTitle = WireUtil.slideOutTitle(movementBrokerage);
        const slideOutWidth = movementBrokerage ? 1300 : 1400;
        const endpointName = WireUtil.ENDPOINT_ELECTRONIC_ADVANCE;
        const layoutProps = { movementId, movementBrokerage, orderId, driverId, driverName, validatedFrom, moveType, issueAdvanceCallback, manifestId };
        const advancesLayout = Layout.getLayout(endpointName, layoutProps as any) as ElectronicAdvances;
        const slideoutDecorator = new SlideoutDecorator({
            title: slideOutTitle,
            layout: advancesLayout,
            fillVerticalSpace: true,
            width: slideOutWidth,
            overlayProps: { greyedBackground: true, closeOnClickOff: false},
            addlComponents: advancesLayout.buttonSave,
            onClose: (cancelled: boolean) => advancesLayout.slideOut()
        });
    }

    private createSaveButton(): Button {
        return new Button({
            rowBreak: false,
            backgroundColor: "primary",
            borderColor: "primary",
            borderWidth: 1,
            fontSize: 14,
            color: "primary.reverse",
            caption: "Issue Advance",
            onClick: (event: ClickEvent) => this.issueAdvance()
        });
    }

    private textboxWireCodeSelectionListener() {
        this.textboxWirecode.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            if (event.hasSelection() === true)
                this.handleTextboxWirecodeSelection(event);
        });
    }

    private textboxMovementIdSelectionListener() {
        this.textboxMovementId.addLookupModelSelectionListener(async (event: LookupModelSelectionEvent) => {
            if (event.hasSelection() === true) {
                const movementId = event.target.text;
                this.textboxManifestId.text = await WireUtil.getManifestIdbyMovementId(movementId);
                const wireCode = this.textboxWirecode.text;
                const orderId = this.textboxOrderId.text;
                const payeeId = this.mainDataSource.activeRow.get("payee_id");
                if (!StringUtil.isEmptyString(wireCode)) {
                    const response = await WireUtil.showWireCode(movementId, wireCode, payeeId);
                    const validWireCode: boolean = response?.valid_wire_code;
                    if (!validWireCode) {
                        this.resetActiveRow();
                        this.textboxAmount.enabled = false;
                    }
                }
                this.updateAdvanceHistory(orderId);
            }
        });
    }

    private textboxOrderIdSelectionListener() {
        this.textboxOrderId.addLookupModelSelectionListener(async (event: LookupModelSelectionEvent) => {
            if (event.hasSelection() === true) {
                const orderId = event.target.text;
                const response = await this.showMovementsIdByOrder(orderId);
                if (response) {
                    this.textboxMovementId.text = response.movement_id;
                    const movementId = this.textboxMovementId.text;
                    this.textboxManifestId.text = await WireUtil.getManifestIdbyMovementId(movementId);
                    const wireCode = this.textboxWirecode.text;
                    const payeeId = this.mainDataSource.activeRow.get("payee_id");
                    if (!StringUtil.isEmptyString(wireCode)) {
                        const response = await WireUtil.showWireCode(movementId, wireCode, payeeId);
                        const validWireCode: boolean = response?.valid_wire_code;
                        if (!validWireCode) {
                            this.resetActiveRow();
                            this.textboxAmount.enabled = false;
                        }
                    }
                }
                this.updateAdvanceHistory(orderId);
            }
        });
    }

    private async updateAdvanceHistory(orderId) {
        if (!this.switchOrderPayeeAdvanceHistory.checked) {
            this.tableOrderPayeeAdvanceHistory.clearRows();
            const driverId = this.mainDataSource.activeRow.get("driver_id");
            const isChecked = this.switchOrderPayeeAdvanceHistory.checked;
            const suffix = isChecked ? " - Payee (Last 7 Days)" : " - Order#" + orderId;
            this.advanceHistoryLabel.text = "Advance History " + suffix;
            await this.sourceWireAdvanceQuickInfo.search({ "order_id": orderId, "driver_id": driverId, "last_seven_days": false , "history_from": "Other" });
        }
    }

    private async showAdvanceHistory(isBrokerageMovement: boolean) {
        if (!isBrokerageMovement) {
            this.enableAdvanceHistory();
            const tableAdvances: Table = this.tableOrderPayeeAdvanceHistory;
            const isChecked = this.switchOrderPayeeAdvanceHistory.checked;
            const response = await this.sourceWireAdvanceQuickInfo.search({ "order_id": this._orderId, "driver_id": this._driverId, "last_seven_days": isChecked, "history_from": "Other" });
            if (response.modelRows.length > 0) {
                tableAdvances.addRowDisplayListener((event: TableRowDisplayEvent) => {
                    const tableRow = event.getTableRow();
                });
            }
        }
    }

    private enableAdvanceHistory() {
        this.advanceHistoryLabel.visible = true;
        this.panelTotalAdvances.visible = true;
        this.panelTotalDeduction.visible = true;
        this.panelOtherCharges.visible = true;
        this.textboxTotalAdvances.visible = true;
        this.textboxTotalDeductions.visible = true;
        this.textboxOtherCharges.visible = true;
        this.switchOrderPayeeAdvanceHistory.visible = true;
        this.tableOrderPayeeAdvanceHistory.visible = true;
        this.switchOrderPayeeAdvanceHistory.checked = this._validatedFrom === "Master Driver";
        this.setColumnVisibility();
    }

    private enhanceWireCodeBeforeLookupModel(validatedFrom: string, isBrokerageMovement: boolean) {
        this.textboxWirecode.addBeforeLookupModelSearchListener((event) => {
            const activeRow = this.activeRow?.data;
            event.filter.driver_id = activeRow.driver_id;
            event.filter.movement_id = isBrokerageMovement ? this._movementId : this.textboxMovementId.text;
            event.filter.order_id = isBrokerageMovement ? this._orderId : this.textboxOrderId.text;
            event.filter.validated_from = validatedFrom;
        });
    }

    private enhanceOrderIdBeforeLookUpModel() {
        this.textboxOrderId.addBeforeLookupModelSearchListener((event) => {
            event.filter.driver_id = this._driverId;
            event.filter.move_type = this._moveType;
            event.filter.order_id = this.textboxOrderId.text;
            event.filter.movement_id = this.textboxMovementId.text;
            event.filter.search_data_from = "Order";
        })
    }
    private enhanceMovementIdBeforeLookUpModel() {
        this.textboxMovementId.addBeforeLookupModelSearchListener((event) => {
            event.filter.driver_id = this._driverId;
            event.filter.move_type = this._moveType;
            event.filter.order_id = this.textboxOrderId.text;
            event.filter.movement_id = this.textboxMovementId.text;
            event.filter.search_data_from = "Movement";
        })
    }

    private enhanceLookupModelForBrokerageMovementPayee(isMovementBrokerage: boolean) {
        this.textboxPayeeId.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            if (isMovementBrokerage && event.hasSelection() === true) {
                const payeeId = event.target.getDataValue();
                this.validatePayee(payeeId)
                    .then(result => {
                        if (!StringUtil.isEmptyString(result)) {
                            Snackbar.showWarningSnackbar(result);
                            this.textboxAmount.enabled = true;
                            this.activeRow.set({ payee_id: null, amount: null });
                        } else {
                            if (!StringUtil.isEmptyString(this.textboxWirecode.text)) {
                                this.textboxAmount.enabled = true;
                                this.activeRow.set({ amount: null });
                            }
                        }
                        const id = event.selection.data.id;
                        this.textboxPayeeId.quickInfoOverrideKeyValue = id;
                    });
                }

        });
    }

    private addBlurListenerToTextboxAmount() {
        this.textboxAmount.addBlurListener(async (event: BlurEvent) => {
            const currencyAmount = (event.target as Textbox).getDataValue() as Currency;
            const wirecode = this.textboxWirecode.getDataValue();
            const carrierId = this.textboxPayeeId.getDataValue();
            const orderId = this.textboxOrderId.getDataValue();
            const brokerageMovement = this._movementBrokerage;
            const movementId = brokerageMovement ? this._movementId : this.textboxMovementId.text;
            if (currencyAmount && currencyAmount.amount) {
                if (currencyAmount.amount > 0) {
                    const response = await this.validateAmount(currencyAmount, carrierId, wirecode, movementId, this._driverId, this._movementBrokerage, orderId);
                    const validAmount: boolean = response?.valid_amount;
                    const result: string = response?.result;
                    if (!validAmount) {
                        Snackbar.showWarningSnackbar(result);
                        this.activeRow.set({ amount: null });
                        this.textboxAmount.focus();
                    }
                } else {
                    Snackbar.showWarningSnackbar("Amount invalid: Must be numeric and postive");
                    this.activeRow.set({ amount: null });
                    this.textboxAmount.focus();
                }
            }
        });
    }

    private addBlurListenerToTextboxWireCode() {
        this.textboxWirecode.addBlurListener((event: BlurEvent) => {
            const isWirecodeBlank = StringUtil.isEmptyString((event.target as Textbox).text);
            if (isWirecodeBlank) {
                this.resetActiveRow();
            }
        });
    }

    textboxMovementIdOnBlur(event: BlurEvent) {
        const movementId = event.target as Textbox;
        if (StringUtil.isEmptyString(movementId.text)) {
            this.textboxManifestId.text = null;
        }
    }

    private async handleTextboxWirecodeSelection(event: LookupModelSelectionEvent): Promise<void> {
        const wirecode = event.target.getDataValue();
        const movementId = this._movementBrokerage ? this._movementId : this.textboxMovementId.getDataValue();
        const moveId = Array.isArray(movementId) ? movementId[0] : movementId;
        const response = await this.validateWirecode(wirecode, moveId, this._driverId);
        const wireCodeValid = response?.is_wire_code_valid;
        const warningMessage = response?.result;
        if (wireCodeValid) {
            const data = event.selection?.data;
            const isOrderRequiredInWireCode = data?.require_order;
            this.activeRow.set({ require_order: isOrderRequiredInWireCode, wirecode: data.id, description: data.descr, deduction: data.create_deduction, other: data.create_other_charge, amount: null });
            this.textboxAmount.enabled = true;
            if (isOrderRequiredInWireCode === 'N' || isOrderRequiredInWireCode === undefined) {
                this.textboxOrderId.enabled = true;
            }
            this.switchDeduction.getDataValue();
            this.switchOther.getDataValue();
        } else {
            Snackbar.showWarningSnackbar(warningMessage);
            this.resetActiveRow();
            this.textboxAmount.enabled = false
            this.switchDeduction.getDataValue();
            this.switchOther.getDataValue();
        }
    }

    private resetActiveRow() : void {
        this.activeRow.set({wirecode: null, description: null, deduction: null, other: null, amount:null})
    }

    private setUpBeforeLookupSearchPayAdvance(movementBrokerage: boolean) {
        if (movementBrokerage) {
            this.textboxPayeeId.addBeforeLookupModelSearchListener(event => { event.filter.pay_advances = 'Y'; });
        }
    }

    public async validatePayee(payeeId: string): Promise<string> {
        const endpoint = WireUtil.ENDPOINT_VALIDATE_PAYEE_ID;
        return await Api.search(endpoint, { "payee_id": payeeId }).then(response => response.data[0]?.result)
    }

    public async validateWirecode(wirecode: string, movementId: string, driverId: string): Promise<any> {
        const endpoint = WireUtil.ENDPOINT_VALIDATE_WIRECODE;
        return await Api.search(endpoint, { "wirecode": wirecode, "movement_id": movementId , "driver_id": driverId})
            .then(response => response.data[0])
    }

    public async showMovementsIdByOrder(order: string): Promise<any> {
        const endpoint = "lme/dispatch/movement-info-by-order";
        return await Api.search(endpoint, {"order_id": order}).then(response => response.data[0]);
    }

    public async validateAmount(amount: Currency, carrierId: string, wirecode: string, movementId: string, driverId: string, isMovementBrokerage: boolean, orderId: string): Promise<any> {
        const endpoint = WireUtil.ENDPOINT_VALIDATE_AMOUNT;
        return await Api.search(endpoint, { "amount": amount, "carrier_id": carrierId, "wirecode": wirecode, "movement_id": movementId, "driver_id": driverId, "movement_brokerage": isMovementBrokerage, "order_id": orderId })
            .then(response => response.data[0])
    }

    private issueAdvance() {
        this.buttonSave.busy = true;
        const payeeId = this.textboxPayeeId.getDataValue();
        const wirecode = this.textboxWirecode.getDataValue();
        const amount = this.textboxAmount.getDataValue();
        const descr = this.textboxDescr.getDataValue();
        const deduction = this.switchDeduction.getDataValue();
        const other = this.switchOther.getDataValue();
        const driverName = this.textboxDriverName.getDataValue();
        const zmitAdvance = this.switchZmitWire.checked;
        const driverId = this._driverId;
        const orderId = this._movementBrokerage ? this._orderId : this.textboxOrderId.text;
        const movementId = this._movementBrokerage ? this._movementId : this.textboxMovementId.text ;
        if (!(amount.amount > 0)) {
            this.buttonSave.busy = false;
            Snackbar.showWarningSnackbar("Amount cannot be zero");
            return;
        }

        const isPayeeValidPromise = this.validatePayee(payeeId);
        const isWirecodeValidPromise = this.validateWirecode(wirecode, movementId, driverId);
        const isAmountValidPromise = this.validateAmount(amount, payeeId, wirecode, movementId, driverId, this._movementBrokerage, orderId);

        const promises = [isPayeeValidPromise, isWirecodeValidPromise, isAmountValidPromise];
        Promise.all(promises)
            .then(values => {
                const payeePromiseResult = StringUtil.isEmptyString(values[0]);
                const wireCodePromiseResult = StringUtil.isEmptyString(values[1].result);
                const amountValidPromiseResult = values[2].valid_amount;
                if (payeePromiseResult&& wireCodePromiseResult&& amountValidPromiseResult) {
                    Api.post(WireUtil.ENDPOINT_ISSUE_ADVANCES,
                        {
                            "movement_id": movementId,
                            "payee_id": payeeId,
                            "wirecode": wirecode,
                            "description": descr,
                            "amount": amount,
                            "deduction": deduction,
                            "other": other,
                            "driver_id": driverId,
                            "order_id": orderId,
                            "driver_name": driverName,
                            "zmit_advance": zmitAdvance
                        }).then(response => {

                            this.buttonSave.busy = false;
                            const { amount, carrier_name, check_code, zmit_error } = response.data[0];
                            const isCheckCodePresent = !StringUtil.isEmptyString(check_code);
                            if (isCheckCodePresent) {
                                this.slideOut();
                                setTimeout(() => {
                                    Toast.showSuccessToast("Success", `Advance issued for ${amount} to ${carrier_name}. Code is ${check_code}`, null);
                                    if (!StringUtil.isEmptyString(zmit_error)) {
                                        Snackbar.showWarningSnackbar(zmit_error);
                                    }
                                    if (this._issueAdvanceCallback) {
                                        this._issueAdvanceCallback();
                                    }
                                }, 500);
                            }
                        })
                        .catch(reason => {
                            this.buttonSave.busy = false;
                            throw reason;
                        })
                } else {
                    this.buttonSave.busy = false;
                    if (!StringUtil.isEmptyString(values[0])) {
                        Snackbar.showWarningSnackbar(values[0])
                        this.activeRow.set({ payee_id: null, amount: null });
                    }
                    if (!StringUtil.isEmptyString(values[1].result)) {
                        Snackbar.showWarningSnackbar(values[1].result);
                        this.resetActiveRow();
                    }
                    if (!StringUtil.isEmptyString(values[2])) {
                        Snackbar.showWarningSnackbar(values[2]);
                        this.activeRow.set({ amount: null });
                    }
                }
            })
    }

    public set issueAdvanceCallback(cb: Function) {
        this._issueAdvanceCallback = cb;
    }

    private addblurListenerToPayeeId() {
        this.textboxPayeeId.addBlurListener((event: BlurEvent) => {
            const payeeIdTextbox = (event.target as Textbox).text;
            const isPayeeIdBlank = payeeIdTextbox == undefined || StringUtil.isEmptyString(payeeIdTextbox);
            if (isPayeeIdBlank) {
                this.activeRow.set({ payee_id: null, amount: null });
                this.textboxAmount.enabled = false;
            }
        });
    }

    private addBlurListenerToTextboxId() {
        this.textboxOrderId.addBlurListener((event: BlurEvent) => {
            const orderId = (event.target as Textbox).text
            if (StringUtil.isEmptyString(orderId) || orderId == undefined) {
                this.activeRow.set({movement_id: null});
                this.activeRow.set({manifest_id: null});
            }
        })
    }

    /** This is an event handler for the onChange event of switchOrderPayeeAdvances. */
    async switchOrderPayeeAdvancesOnChange() {
        if (this.mainDataSource.activeRow !== undefined) {
            this.setColumnVisibility();
            const isChecked = this.switchOrderPayeeAdvanceHistory.checked;
            const orderId = this.textboxOrderId.text;
            const driverId = this.mainDataSource.activeRow.get("driver_id");
            const suffix = isChecked ? " - Payee (Last 7 Days)" : " - Order#" + orderId;
            this.advanceHistoryLabel.text = "Advance History " + suffix;
            await this.sourceWireAdvanceQuickInfo.search({"order_id": this.textboxOrderId.text, "driver_id": driverId, "last_seven_days": isChecked, "history_from": "Other"});
        }
    }

    textboxAdvanceHistoryCheckAmountOnPrintable(event: PrintableEvent) {
        const textbox = event.target;
        textbox.fillRow = true;
    }

    private setColumnVisibility() {
        const isViewByPayee = this.switchOrderPayeeAdvanceHistory.checked;
        const orderColumn = this.tableOrderPayeeAdvanceHistory.getColumnByCaption("Order");
        const payeeColumn = this.tableOrderPayeeAdvanceHistory.getColumnByCaption("Payee");
        this.tableOrderPayeeAdvanceHistory.setColumnVisibility(orderColumn, isViewByPayee);
        this.tableOrderPayeeAdvanceHistory.setColumnVisibility(payeeColumn, !isViewByPayee);
    }

}
